import { useCallback, useRef } from "react";
import type { MktVersionTypes } from "../components/mkt/mktVersionUtils";
import { getAdChannel } from "routes/adFunnel";
import { isAdTrackingSuppressed } from "analytics/adTracking";

/**
 * Google Ads 이벤트 타입
 */
export type GtagEventType =
  | "InitiateCheckout"
  | "Purchase"
  | "CompleteRegistration";

/**
 * 버전별 이벤트 타입별 label 매핑
 * 형식: AW-10862597581/{label}
 */
const EVENT_LABELS: Record<string, Record<GtagEventType, string>> = {
  v4: {
    InitiateCheckout: "AW-10862597581/xT-UCK6P8NIbEM2r2Lso",
    Purchase: "AW-10862597581/K-jaCOWc8NIbEM2r2Lso",
    CompleteRegistration: "AW-10862597581/8zVjCMu-_tIbEM2r2Lso",
  },
};

interface GtagConversionParams {
  transactionId?: string;
  value?: number;
  currency?: string;
  email?: string;
  /** 서버 해시(정규화 후 SHA-256 hex). 있으면 평문 email 대신 사용 */
  sha256EmailAddress?: string;
  /** 서버 해시(E.164 정규화 후 SHA-256 hex). 카카오 가입자는 전화번호 필수 동의라 대부분 존재 */
  sha256PhoneNumber?: string;
  /** Google Ads 신규 고객 확보 목표용 — Purchase 에서만 의미 있음 */
  newCustomer?: boolean;
  /**
   * 내부자·제휴 프로모션 제외 판정용 (analytics/adTracking). 호출부가 가진 유저 객체를 그대로 넘긴다 —
   * 가입 시점엔 userSimpleAtom 이 아직 갱신 전이라 로그인 응답(result)을 넘겨야 한다.
   */
  user?: { isInsider?: boolean; promotion?: string } | null;
}

/**
 * Google Ads 전환 이벤트를 전송하는 훅
 */
export default function useGtagConversion() {
  // 결제 버튼 클릭(InitiateCheckout)은 마운트당 1회만 — 토스 창을 닫고 재클릭해도 전환이 쌓이지 않도록.
  // 재진입은 새 마운트라 다시 나가며, 그건 Google Ads 전환 액션의 횟수 "1회" 설정이 거른다.
  const initiateCheckoutSentRef = useRef(false);

  const trackEvent = useCallback(
    (
      eventType: GtagEventType,
      version: MktVersionTypes,
      params?: GtagConversionParams
    ) => {
      // gtag 전환은 gads 퍼널(Google Ads) 전용 — 메타 퍼널에서는 보내지 않는다 (routes/adFunnel.ts)
      if (getAdChannel() !== "google") return;

      // 내부자·제휴 프로모션 유저는 메인 앱의 Firebase/Meta 와 같은 기준으로 제외 — 테스트 결제가 학습 데이터에 섞이지 않도록
      if (isAdTrackingSuppressed(params?.user)) return;

      if (!window.gtag) {
        console.warn("[gtag] gtag is not initialized");
        return;
      }

      const label = EVENT_LABELS[version]?.[eventType];
      if (!label) {
        console.warn(
          `[gtag] No label found for event ${eventType} version ${version}`
        );
        return;
      }

      if (eventType === "InitiateCheckout") {
        if (initiateCheckoutSentRef.current) return;
        initiateCheckoutSentRef.current = true;
      }

      // 향상된 전환(Enhanced Conversions) — Google 문서 규격대로 전환 이벤트 직전에 user_data 를 set 한다.
      // 서버 해시가 있으면 그대로(sha256_*), 없으면 평문 email 을 넘겨 gtag 가 정규화·해싱하게 한다.
      const userData: Record<string, string> = {};
      if (params?.sha256EmailAddress) {
        userData.sha256_email_address = params.sha256EmailAddress;
      } else if (params?.email) {
        userData.email = params.email;
      }
      if (params?.sha256PhoneNumber) {
        userData.sha256_phone_number = params.sha256PhoneNumber;
      }
      if (Object.keys(userData).length > 0) {
        window.gtag("set", "user_data", userData);
      }

      const eventParams: any = {
        send_to: label,
        transaction_id: params?.transactionId ?? "",
        value: params?.value,
        currency: params?.currency,
      };

      if (params?.newCustomer !== undefined) {
        eventParams.new_customer = params.newCustomer;
      }

      window.gtag("event", "conversion", eventParams);

      console.log(`[gtag] Tracked ${eventType} (${version}):`, label, params);
    },
    []
  );

  return trackEvent;
}
